import React, { useEffect, useState, useRef, useCallback } from "react";
import JsSIP from "jssip";
import fetch from "../../utils/fetch";
import Cookies from "../../utils/cookies";

interface SipCredentials {
    sipUser: string;
    sipPass: string;
    sipDomain: string;
    wssUri: string;
}

interface Participant {
    id: string;
    caller_id_name?: string;
    caller_id_number?: string;
    muted?: boolean;
    can_hear?: boolean;
}

// ─── Hard-coded DIDs ───────────────────────────────────────────────────────
const DIDS: any = [
    // { number: "+61280739217", label: "Main Line" },
    // { number: "+61290510564", label: "Sales" },
];

const CONFERENCE_API_BASE = "https://siptestea.ypc.solutions/api/conference";

// ─── Minimal WebPhone ──────────────────────────────────────────────────────
const WebPhone: React.FC<{ credentials: SipCredentials }> = ({ credentials }) => {
    const { sipUser, sipPass, sipDomain, wssUri } = credentials;

    const [reg, setReg] = useState("init"); // init | ok | fail | gone
    const [call, setCall] = useState("idle"); // idle | out | in | active
    const [dialValue, setDialValue] = useState("");
    const [remoteId, setRemoteId] = useState("");
    const [secs, setSecs] = useState(0);

    // ── Conference / call-control state ────────────────────────────────────
    const [participants, setParticipants] = useState<Participant[]>([]);
    const [showParticipants, setShowParticipants] = useState(false);
    const [addNumber, setAddNumber] = useState("");
    const [recording, setRecording] = useState(false);
    const [confBusy, setConfBusy] = useState(false);
    const [confError, setConfError] = useState("");
    // Separate from confBusy: confBusy only covers the HTTP round trip
    // (now near-instant, since /add returns before the dial resolves), but
    // dialingNumber covers the whole "is anyone currently being dialed"
    // window — that's what actually needs to block a second Add click.
    const [dialingNumber, setDialingNumber] = useState<string | null>(null);
    // Set once everyone except this browser's own leg has left a conference
    // that previously had other people in it — watched by an effect below
    // (defined after hangup) so it can end the call automatically.
    const [roomIsEmpty, setRoomIsEmpty] = useState(false);

    const uaRef = useRef<any>(null);
    const sessRef = useRef<any>(null);
    const audioRef = useRef<HTMLAudioElement>(null);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const dialPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const dialTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const roomWatchRef = useRef<ReturnType<typeof setInterval> | null>(null);
    // True once another participant has actually joined at least once —
    // guards against the room-watch firing during the normal wait for the
    // very first added person to pick up, when "no one else here yet" is
    // expected rather than a sign everyone left.
    const everHadOtherRef = useRef(false);

    // ── Conference API helper ───────────────────────────────────────────────
    // Authorization/xAccessToken below are just sipUser/sipPass reused —
    // same field names and same identity already used by
    // /user/vca/sip/credentials, just reused here. Uses window.fetch
    // explicitly since the bare `fetch` name is shadowed by the utils/fetch
    // import above; swap this for that utility once its body-passing
    // convention is confirmed, if you'd rather keep everything consistent.
    const confApi = useCallback(
        async (path: string, body: Record<string, any> = {}) => {
            setConfError("");
            try {
                const res = await window.fetch(`${CONFERENCE_API_BASE}${path}`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ Authorization: sipUser, xAccessToken: sipPass, ...body }),
                });
                const data = await res.json();
                if (!res.ok) throw new Error(data?.error || `Request failed (${res.status})`);
                return data;
            } catch (e: any) {
                console.error("[CONF]", path, e.message);
                setConfError(e.message);
                throw e;
            }
        },
        [sipUser, sipPass],
    );

    // Field names (`members`, `caller_id_name`, `muted`, `can_hear`) are
    // confirmed against real `conference <room> list json` output — see
    // server.js's getConferenceMembers for the actual parser.
    const loadParticipants = useCallback(async () => {
        try {
            const data = await confApi("/participants");
            const members = data.members || [];
            setParticipants(members);
            return members;
        } catch {
            /* error already captured in confError */
            return null;
        }
    }, [confApi]);

    const stopDialingWatch = useCallback(() => {
        if (dialPollRef.current) clearInterval(dialPollRef.current);
        if (dialTimeoutRef.current) clearTimeout(dialTimeoutRef.current);
        dialPollRef.current = null;
        dialTimeoutRef.current = null;
        setDialingNumber(null);
    }, []);

    const stopRoomWatch = useCallback(() => {
        if (roomWatchRef.current) clearInterval(roomWatchRef.current);
        roomWatchRef.current = null;
    }, []);

    // Runs for the rest of the call once a conference exists (started from
    // handleAddParticipant below). caller_id_number === sipUser identifies
    // this browser's own leg — confirmed against real conference member
    // data, same identity check /leave already uses server-side.
    const startRoomWatch = useCallback(() => {
        if (roomWatchRef.current) return; // already watching
        roomWatchRef.current = setInterval(async () => {
            const members = await loadParticipants();
            if (!members) return; // transient fetch error — try again next tick
            const others = members.filter((m: any) => m.caller_id_number !== sipUser);
            if (others.length > 0) {
                everHadOtherRef.current = true;
                return;
            }
            if (everHadOtherRef.current) {
                setRoomIsEmpty(true);
            }
        }, 8000);
    }, [loadParticipants, sipUser]);

    const handleAddParticipant = async () => {
        const n = addNumber.trim();
        if (!n || dialingNumber) return; // a dial is already in flight
        setConfBusy(true);
        const countBefore = participants.length;
        try {
            await confApi("/add", { number: n });
            setAddNumber("");
            setShowParticipants(true);
            setDialingNumber(n);
            await loadParticipants(); // shows any existing members right away
            startRoomWatch(); // safe to call again on a 2nd/3rd add — no-ops if already running

            // The server responds as soon as the call is promoted to a
            // conference, before the new leg has actually answered, so
            // there's no single signal for "they joined" — poll instead.
            // This also drives clearing dialingNumber, which re-enables Add.
            dialPollRef.current = setInterval(async () => {
                const members = await loadParticipants();
                if (members && members.length > countBefore) {
                    stopDialingWatch();
                }
            }, 3000);

            // Mirrors the server's originate_timeout=25 plus margin — give
            // up waiting and let Add be used again if no one joined by then.
            dialTimeoutRef.current = setTimeout(stopDialingWatch, 28000);
        } catch {
            /* surfaced via confError */
        } finally {
            setConfBusy(false);
        }
    };

    const handleMemberAction = async (path: string, memberId: string) => {
        setConfBusy(true);
        try {
            await confApi(path, { memberId });
            await loadParticipants();
        } catch {
            /* surfaced via confError */
        } finally {
            setConfBusy(false);
        }
    };

    const handleToggleRecording = async () => {
        setConfBusy(true);
        try {
            if (recording) {
                await confApi("/record/stop");
                setRecording(false);
            } else {
                await confApi("/record/start");
                setRecording(true);
            }
        } catch {
            /* surfaced via confError */
        } finally {
            setConfBusy(false);
        }
    };

    const attachTrack = useCallback((pc: any) => {
        if (!pc || pc._tracksAttached) return; // guard against double-attaching
        pc._tracksAttached = true;
        console.log("[SIP] attaching ontrack — signalingState=", pc.signalingState);
        pc.ontrack = ({ streams: [s] }: any) => {
            console.log("[SIP] ontrack  stream=", s?.id);
            if (audioRef.current && s) {
                audioRef.current.srcObject = s;
                audioRef.current.play().catch((e: any) => console.error("[SIP] play() failed:", e));
            }
        };
        pc.oniceconnectionstatechange = () => console.log("[SIP] ICE state →", pc.iceConnectionState);
        pc.onconnectionstatechange = () => console.log("[SIP] connection state →", pc.connectionState);
    }, []);

    const wireSession = useCallback(
        (sess: any) => {
            console.log("[SIP] wireSession  connection already exists=", !!sess.connection);
            if (sess.connection) attachTrack(sess.connection);
            sess.on("peerconnection", ({ peerconnection: pc }: any) => {
                console.log("[SIP] peerconnection event fired");
                attachTrack(pc);
            });
            sess.on("connecting", () => console.log("[SIP] connecting"));
            sess.on("progress", (e: any) => console.log("[SIP] progress", e?.response?.status_code));
            sess.on("confirmed", async () => {
                console.log("[SIP] confirmed — call active");
                setCall("active");
                setSecs(0);
                timerRef.current = setInterval(() => setSecs((n) => n + 1), 1000);

                // Promote every call into its conference room immediately,
                // not just once someone's added — this is what makes
                // mute/hold work on a plain 1:1 call. Starting the room
                // watch here too (not only in handleAddParticipant) is what
                // makes "everyone else left" detection work for a plain
                // 1:1 call, not just ones that had a participant added.
                try {
                    await confApi("/promote");
                    await loadParticipants();
                    startRoomWatch();
                } catch {
                    /* surfaced via confError; the call itself still proceeds */
                }
            });
            const end = (e?: any) => {
                const cause = e?.cause || e?.message || "unknown";
                const code = e?.response?.status_code || "";
                console.log("[SIP] ended/failed  cause=", cause, " code=", code, e);
                if (timerRef.current) clearInterval(timerRef.current);
                stopDialingWatch();
                stopRoomWatch();
                everHadOtherRef.current = false;
                setCall("idle");
                setRemoteId("");
                setSecs(0);
                sessRef.current = null;
                if (audioRef.current) audioRef.current.srcObject = null;
                // Conference state is call-scoped — clear it so the next
                // call starts fresh rather than showing stale participants.
                setParticipants([]);
                setShowParticipants(false);
                setRecording(false);
                setConfError("");
            };
            sess.on("ended", end);
            sess.on("failed", end);
        },
        [attachTrack, stopDialingWatch, stopRoomWatch, confApi, loadParticipants, startRoomWatch],
    );

    useEffect(() => {
        JsSIP.debug.disable("JsSIP:*");
        const ua = new JsSIP.UA({
            sockets: [new JsSIP.WebSocketInterface(wssUri)],
            uri: `sip:${sipUser}@${sipDomain}`,
            password: sipPass,
            register: true,
            session_timers: false,
        });
        ua.on("registered", () => setReg("ok"));
        ua.on("unregistered", () => setReg("gone"));
        ua.on("registrationFailed", (e: any) => {
            console.error("SIP reg failed", e);
            setReg("fail");
        });
        ua.on("newRTCSession", ({ session }: any) => {
            sessRef.current = session;
            wireSession(session);
            if (session.direction === "incoming") {
                setRemoteId(session.remote_identity?.uri?.user || "Unknown");
                setCall("in");
            }
        });
        ua.start();
        uaRef.current = ua;
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            if (dialPollRef.current) clearInterval(dialPollRef.current);
            if (dialTimeoutRef.current) clearTimeout(dialTimeoutRef.current);
            if (roomWatchRef.current) clearInterval(roomWatchRef.current);
            ua.stop();
        };
    }, [sipUser, sipPass, sipDomain, wssUri, wireSession]);

    const dial = (target?: string) => {
        const t = (target || dialValue).trim();
        if (!t || !uaRef.current || reg !== "ok") return;
        uaRef.current.call(`sip:${t}@${sipDomain}`, {
            mediaConstraints: { audio: true, video: false },
            pcConfig: { iceServers: [{ urls: "stun:stun.l.google.com:19302" }] },
        });
        setRemoteId(t);
        setCall("out");
    };
    const hangup = () => {
        // This doubles as "leave the call" once a call has been promoted
        // into a multi-party conference: it only ends YOUR OWN SIP session.
        // FreeSWITCH conferences keep running as long as anyone's still in
        // them, so whoever else is on the call stays connected — no separate
        // "Leave" action needed.
        console.log("[HANG] sessRef=", sessRef.current, "callState=", call);
        if (!sessRef.current) {
            console.warn("[HANG] no session");
            return;
        }
        sessRef.current.terminate();
    };

    // Separate from hangup() itself so the room-watch above doesn't need a
    // reference to hangup (which would create an ordering dependency on
    // where each is defined) — this just reacts to the flag instead.
    useEffect(() => {
        if (roomIsEmpty) {
            console.log("[CONF] everyone else has left the conference — ending call");
            stopRoomWatch();
            setRoomIsEmpty(false);
            hangup();
        }
    }, [roomIsEmpty]);
    const [lastDtmf, setLastDtmf] = React.useState("");
    const sendDTMF = (digit: string) => {
        if (!sessRef.current) return;
        try {
            sessRef.current.sendDTMF(digit, { transportType: "INFO", duration: 160, interToneGap: 50 });
            setLastDtmf(digit);
            setTimeout(() => setLastDtmf(""), 600);
            console.log("[DTMF] sent:", digit);
        } catch (e: any) {
            console.error("[DTMF] error:", e.message);
        }
    };
    const answer = () =>
        sessRef.current?.answer({
            mediaConstraints: { audio: true, video: false },
            pcConfig: { iceServers: [{ urls: "stun:stun.l.google.com:19302" }] },
        });
    const reject = () => sessRef.current?.terminate({ status_code: 486, reason_phrase: "Busy Here" });

    const fmt = (s: number) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

    const s: Record<string, React.CSSProperties> = {
        wrap: { position: "fixed", bottom: 20, right: 20, background: "#1a1a2e", color: "#fff", padding: 16, borderRadius: 12, width: 280, fontFamily: "monospace", fontSize: 13, zIndex: 9999, boxShadow: "0 8px 32px rgba(0,0,0,.5)" },
        row: { display: "flex", gap: 8, marginBottom: 8 },
        input: { flex: 1, padding: "6px 10px", borderRadius: 6, border: "1px solid #444", background: "#111", color: "#fff", fontSize: 13 },
        btn: { padding: "6px 12px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600 },
        didBtn: { flex: 1, padding: "6px 8px", borderRadius: 6, border: "1px solid #333", background: "#111", color: "#aaa", cursor: "pointer", fontSize: 11, textAlign: "left" as const },
        status: { fontSize: 11, color: reg === "ok" ? "#3ecf8e" : reg === "fail" ? "#ff4d6a" : "#f59e0b", marginBottom: 8 },
        divider: { borderTop: "1px solid #333", margin: "8px 0", fontSize: 10, color: "#555", paddingTop: 6 },
        confRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0", fontSize: 11 },
        smallBtn: { padding: "3px 6px", borderRadius: 4, border: "none", cursor: "pointer", fontSize: 10, fontWeight: 600, marginLeft: 4 },
    };

    return (
        <div style={s.wrap}>
            <audio ref={audioRef} autoPlay />
            <div style={s.status}>
                ● {reg === "ok" ? "Registered" : reg === "fail" ? "Registration failed" : reg === "gone" ? "Disconnected" : "Connecting…"}
                {" · "}
                {sipUser}@{sipDomain}
            </div>

            {call === "idle" && (
                <>
                    {/* DID quick-dial */}
                    <div style={s.divider}>Quick dial</div>
                    {DIDS.map((d: any) => (
                        <div key={d.number} style={{ ...s.row, marginBottom: 6 }}>
                            <button style={s.didBtn} onClick={() => dial(d.number)} disabled={reg !== "ok"}>
                                <span style={{ color: "#fff" }}>{d.label}</span>
                                <br />
                                <span>{d.number}</span>
                            </button>
                            <button style={{ ...s.btn, background: "#3ecf8e", color: "#fff" }} onClick={() => dial(d.number)} disabled={reg !== "ok"}>
                                Call
                            </button>
                        </div>
                    ))}

                    {/* Free-type input */}
                    <div style={s.divider}>Or type a number</div>
                    <div style={s.row}>
                        <input style={s.input} value={dialValue} onChange={(e) => setDialValue(e.target.value)} placeholder="+61..." onKeyDown={(e) => e.key === "Enter" && dial()} />
                        <button style={{ ...s.btn, background: "#3ecf8e", color: "#fff" }} onClick={() => dial()} disabled={!dialValue || reg !== "ok"}>
                            Call
                        </button>
                    </div>
                </>
            )}

            {call === "in" && (
                <div>
                    <div style={{ marginBottom: 8 }}>📞 Incoming: {remoteId}</div>
                    <div style={s.row}>
                        <button style={{ ...s.btn, background: "#3ecf8e", color: "#fff", flex: 1 }} onClick={answer}>
                            Answer
                        </button>
                        <button style={{ ...s.btn, background: "#ff4d6a", color: "#fff", flex: 1 }} onClick={reject}>
                            Reject
                        </button>
                    </div>
                </div>
            )}

            {call === "out" && (
                <div>
                    <div style={{ marginBottom: 8 }}>Calling {remoteId}…</div>
                    <button style={{ ...s.btn, background: "#ff4d6a", color: "#fff", width: "100%" }} onClick={hangup}>
                        Cancel
                    </button>
                </div>
            )}

            {call === "active" && (
                <div>
                    <div style={{ marginBottom: 8, color: "#3ecf8e" }}>
                        ● On call: {remoteId} · {fmt(secs)}
                    </div>

                    {/* DTMF keypad */}
                    <div style={{ textAlign: "center", fontSize: 22, minHeight: 32, marginBottom: 6, letterSpacing: 4, color: "#3ecf8e" }}>{lastDtmf || " "}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, marginBottom: 10 }}>
                        {["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"].map((d) => (
                            <button key={d} style={{ ...s.btn, background: "#1e1e3a", color: "#fff", padding: "12px 0", fontSize: 18 }} onClick={() => sendDTMF(d)}>
                                {d}
                            </button>
                        ))}
                    </div>

                    {/* ── Conference controls ───────────────────────────── */}
                    <div style={s.divider}>Conference</div>

                    {confError && <div style={{ color: "#ff4d6a", fontSize: 10, marginBottom: 6 }}>{confError}</div>}
                    {dialingNumber && <div style={{ color: "#f59e0b", fontSize: 10, marginBottom: 6 }}>● Dialing {dialingNumber}…</div>}

                    <div style={s.row}>
                        <input style={s.input} value={addNumber} onChange={(e) => setAddNumber(e.target.value)} placeholder="Add caller +61..." onKeyDown={(e) => e.key === "Enter" && handleAddParticipant()} disabled={!!dialingNumber} />
                        <button style={{ ...s.btn, background: dialingNumber ? "#444" : "#3ecf8e", color: "#fff" }} onClick={handleAddParticipant} disabled={!addNumber || confBusy || !!dialingNumber}>
                            {dialingNumber ? "Dialing…" : "Add"}
                        </button>
                    </div>

                    <div style={s.row}>
                        <button
                            style={{ ...s.btn, background: "#1e1e3a", color: "#fff", flex: 1 }}
                            onClick={() => {
                                const next = !showParticipants;
                                setShowParticipants(next);
                                if (next) loadParticipants();
                            }}>
                            {showParticipants ? "Hide" : "Show"} participants {participants.length ? `(${participants.length})` : ""}
                        </button>
                        <button style={{ ...s.btn, background: recording ? "#ff4d6a" : "#1e1e3a", color: "#fff", flex: 1 }} onClick={handleToggleRecording} disabled={confBusy}>
                            {recording ? "⏹ Stop rec" : "⏺ Record"}
                        </button>
                    </div>

                    {showParticipants && (
                        <div style={{ background: "#111", borderRadius: 6, padding: 6, marginBottom: 8 }}>
                            {participants.length === 0 && <div style={{ fontSize: 10, color: "#666" }}>No participant data yet.</div>}
                            {participants.map((p) => (
                                <div key={p.id} style={s.confRow}>
                                    <span>{p.caller_id_number === sipUser ? "You" : p.caller_id_number || p.caller_id_name || p.id}</span>
                                    <span>
                                        <button style={{ ...s.smallBtn, background: "#444", color: "#fff" }} onClick={() => handleMemberAction(p.muted ? "/unmute" : "/mute", p.id)} disabled={confBusy}>
                                            {p.muted ? "Unmute" : "Mute"}
                                        </button>
                                        <button style={{ ...s.smallBtn, background: "#444", color: "#fff" }} onClick={() => handleMemberAction(p.can_hear === false ? "/unhold" : "/hold", p.id)} disabled={confBusy}>
                                            {p.can_hear === false ? "Unhold" : "Hold"}
                                        </button>
                                        <button style={{ ...s.smallBtn, background: "#ff4d6a", color: "#fff" }} onClick={() => handleMemberAction("/kick", p.id)} disabled={confBusy}>
                                            Remove
                                        </button>
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                    <button style={{ ...s.btn, background: "#ff4d6a", color: "#fff", width: "100%", padding: "10px 0", fontSize: 14 }} onClick={hangup}>
                        ✕ Hang Up / Leave
                    </button>
                </div>
            )}
        </div>
    );
};

// ─── Dashboard ─────────────────────────────────────────────────────────────
const Dashboard: React.FC = () => {
    const [ready, setReady] = useState(false);
    const [sipCredentials, setSipCreds] = useState<SipCredentials | null>(null);
    const [error, setError] = useState("");

    useEffect(() => {
        (async () => {
            // 1 — Auth check
            const auth = await new fetch(null, {}, { success: false, error: true }).auth();
            if (!auth || auth.status !== "success") {
                window.location.href = "/login";
                return;
            }

            // 2 — SIP credentials
            // Tokens live in cookies on the dashboard domain — pass them
            // explicitly in the body so they cross the domain boundary.
            const cookie = new Cookies();
            const sip = await new fetch("https://siptestea.ypc.solutions/user/vca/sip/credentials", { Authorization: cookie.get("token"), xAccessToken: cookie.get("access_token") }, { success: false, error: false }).post();
            if (sip?.status === "success") {
                setSipCreds(sip.data);
            } else {
                setError(`SIP credentials failed: ${JSON.stringify(sip)}`);
            }

            setReady(true);
        })();
    }, []);

    if (!ready) return <div style={{ padding: 20, fontFamily: "monospace" }}>Loading…</div>;

    return (
        <div style={{ padding: 20 }}>
            <h2>SIP Test</h2>
            {error && <pre style={{ color: "red" }}>{error}</pre>}
            {sipCredentials ? <WebPhone credentials={sipCredentials} /> : <p>No SIP credentials — check the server logs and SIP_USERS env var.</p>}
        </div>
    );
};

export default Dashboard;
